import { IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FC, useEffect, useRef } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import clsx from 'clsx';
import {
  secondsToShortString,
  secondsToHoursTwoDecimals,
} from 'packages/lib/utils/time';
import CopyToken from 'packages/lib/common-ui/copy-token';
import { useMapState } from 'packages/lib/utils/hooks/use-map-state';
import { Typography } from 'antd';
import { formatDateTime } from 'packages/lib/utils/date';
import { CopyOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';

const useLightTableStyles = makeStyles<{
  rowHeight: number;
}>()((_theme, { rowHeight }) => ({
  table: {
    borderCollapse: 'collapse',
    '& th, tr': {
      textAlign: 'left',
    },
    '& td': {
      borderStyle: 'solid',
      borderColor: 'lightgray',
      borderWidth: '1px 0px',
      borderLeft: '0px',
      borderRight: '0px',
    },
    '& tr:first-of-type td': {
      borderTop: '0px',
    },
    '& tr:last-child td': {
      borderBottom: '0px',
    },
  },
  key: {
    fontWeight: 600,
    padding: '8px 14px',
  },
  value: {
    paddingRight: '10px',
    width: '100%',
  },
  wordBreak: {
    wordBreak: 'break-word',
    paddingTop: rowHeight,
    paddingBottom: rowHeight,
  },
  ellipsis: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  openInNewIcon: {
    position: 'relative',
    top: '3px',
    height: '20px',
    paddingTop: '4px',
  },
}));

export type KeyValueObj = Record<
  string | number,
  string | number | boolean | null
>;

export type KeyValueTableDataType =
  | string
  | string[]
  | KeyValueObj
  | number
  | null
  | undefined;

export interface KeyValueTableRow {
  key: string;
  value: KeyValueTableDataType;
  type?: 'seconds' | 'hoursTwoDecimals' | 'link' | 'formattedTimesstamp'; // present if special formatting is needed for this col's data
  noWrap?: boolean;
  allowCopy?: boolean;
  maxStrLen?: number; // number of characters to truncate to
}
interface KeyValueTableProps {
  tableNameKey: string;
  data: KeyValueTableRow[];
  keyColWidth: number;
  maxValueWidth: number;
  noLines?: boolean;
  rowHeight?: number;
}

/** 
  For table data, each row has an option 'type' that can be any of: 'seconds' | 'hoursTwoDecimals' | 'link' | 'formattedTimesstamp'
  Each row can also have these optional booleans: noWrap, allowCopy

  @param rowHeight specifies the whitespace padding for each row. Use 1 for more compact tables.
*/
export const KeyValueTable: FC<React.PropsWithChildren<KeyValueTableProps>> = ({
  data,
  keyColWidth,
  maxValueWidth,
  tableNameKey,
  rowHeight = 10,
}) => {
  const { classes: tableStyles } = useLightTableStyles({
    rowHeight,
  });

  const [rowHasEllipsis, setRowHasEllipsis] = useMapState<string, boolean>(
    new Map()
  );
  const [expandedRows, setExpandedRowsKey] = useMapState<string, boolean>(
    new Map()
  );

  return (
    <table data-testid="dag-stats-table" className={tableStyles.table}>
      <tbody>
        {data.map((row) => (
          <tr key={tableNameKey + '-' + row.key}>
            <KeyValueCells
              row={row}
              expandedRows={expandedRows}
              tableStyles={tableStyles}
              setExpandedRowsKey={setExpandedRowsKey}
              rowHasEllipsis={rowHasEllipsis}
              setRowHasEllipsis={setRowHasEllipsis}
              keyColWidth={keyColWidth}
              maxValueWidth={maxValueWidth}
            />
          </tr>
        ))}
      </tbody>
    </table>
  );
};

interface MyNewFCProps {
  row: KeyValueTableRow;
  expandedRows: Map<string, boolean>;
  setExpandedRowsKey: (key: string, value: boolean) => void;
  rowHasEllipsis: Map<string, boolean>;
  setRowHasEllipsis: (key: string, value: boolean) => void;
  tableStyles: Record<string, string>;
  keyColWidth: number;
  maxValueWidth: number;
}

export const KeyValueCells: FC<React.PropsWithChildren<MyNewFCProps>> = ({
  row,
  expandedRows,
  setExpandedRowsKey,
  rowHasEllipsis,
  setRowHasEllipsis,
  tableStyles,
  keyColWidth,
  maxValueWidth,
  children,
}) => {
  const tdRef = useRef<HTMLTableCellElement>(null);

  const theme = useRecoilValue(uiThemeAtom);
  const linkBlueColor = theme === 'dark' ? '#6babfeff' : 'default';
  const copyIcon = <CopyOutlined style={{ color: linkBlueColor }} />;

  // This is how we can tell if a CSS Ellipsis is being shown in the Value cell, due to a line being too long, and being clipped with an ellipsis.
  // This lets us show an expand triangle for that item when appropriate.
  useEffect(() => {
    if (tdRef.current) {
      const hasEllipsis =
        tdRef.current?.scrollWidth > tdRef.current?.clientWidth;
      setRowHasEllipsis(row.key, hasEllipsis);
    }
  }, [row.key, tdRef, setRowHasEllipsis]);

  return (
    <>
      <td className={tableStyles.key} style={{ minWidth: keyColWidth }}>
        <span>{row.key}</span>
        {typeof row.value === 'object' &&
          row.value !== null &&
          rowHasEllipsis.get(row.key) && (
            <IconButton
              size="small"
              onClick={(): void =>
                setExpandedRowsKey(row.key, !expandedRows.get(row.key))
              }
            >
              {expandedRows.get(row.key) ? (
                <ArrowDownIcon style={{ fontSize: '1.2rem' }} />
              ) : (
                <ArrowRightIcon style={{ fontSize: '1.2rem' }} />
              )}
            </IconButton>
          )}
      </td>
      <td
        ref={tdRef}
        style={{
          maxWidth:
            typeof row.value === 'object' && !expandedRows.get(row.key)
              ? maxValueWidth
              : undefined,
        }}
        className={clsx(
          tableStyles.value,
          row.noWrap ||
            (typeof row.value === 'object' && !expandedRows.get(row.key))
            ? tableStyles.ellipsis
            : tableStyles.wordBreak
        )}
      >
        {row.type === 'seconds' &&
          secondsToShortString(row.value as string, '--')}
        {row.type === 'hoursTwoDecimals' &&
          secondsToHoursTwoDecimals(row.value as string, '--')}
        {row.type === 'link' && (
          <>
            {row.value ? (
              <a href={row.value as string} target="_blank" rel="noreferrer">
                Link
                <OpenInNewIcon className={tableStyles.openInNewIcon} />
              </a>
            ) : (
              '--'
            )}
          </>
        )}
        {typeof row.value === 'object' && row.value !== null && (
          <>
            <CopyToken
              copyText={JSON.stringify(row.value)}
              label={row.key}
              iconSize="16px"
            />

            {JSON.stringify(row.value)}
          </>
        )}
        {row.type === 'formattedTimesstamp' && (
          <Typography.Text
            copyable={{ icon: copyIcon, text: row.value?.toString() }}
          >
            {row.value
              ? formatDateTime(new Date(Number(row.value) / 1000))
              : '-'}
          </Typography.Text>
        )}
        {row.type !== 'seconds' &&
          row.type !== 'hoursTwoDecimals' &&
          row.type !== 'link' &&
          row.type !== 'formattedTimesstamp' &&
          (typeof row.value !== 'object' || row.value === null) && (
            <>
              {row.value ? (
                <span>
                  <Typography.Text
                    copyable={
                      row.allowCopy || row.noWrap
                        ? { text: row.value?.toString(), icon: copyIcon }
                        : false
                    }
                  >
                    {row.maxStrLen && typeof row.value === 'string'
                      ? row.value.substring(0, row.maxStrLen) + '...'
                      : row.value}
                  </Typography.Text>
                </span>
              ) : (
                '--'
              )}
            </>
          )}
      </td>
    </>
  );
};
